import { useEffect, useState } from 'react';
import {
  Timestamp,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  AuditAction,
  Currency,
  SubscriptionStatus,
  type Period,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import {
  directMonthlyRevenue,
  isDirectLiveSale,
  monthlyValue,
  parseSubscriptionDoc,
  parseSubscriptionDocs,
  reportSubscriptionDrops,
  type SubscriptionDrop,
  type SubscriptionRevenueFields,
} from '@/features/dashboard-finance/subscriptionRevenue';
import { isReportExcludedTenant } from '@/features/tenants/testTenantFilter';
import { serverNow } from '@etiquetachef/ui-shared/online';

const BRT_OFFSET_MS = 3 * 60 * 60 * 1000;

function brtRangeUtcMs(period: Period): { startUtcMs: number; endUtcMs: number } {
  const startBrt = Date.parse(`${period.from}T00:00:00Z`);
  const endBrt = Date.parse(`${period.to}T00:00:00Z`) + 24 * 60 * 60 * 1000;
  return {
    startUtcMs: startBrt + BRT_OFFSET_MS,
    endUtcMs: endBrt + BRT_OFFSET_MS,
  };
}

/**
 * Linha da tabela de empresas criadas no período. `priceCents`/`usdEqCents`
 * são o EQUIVALENTE MENSAL do valor contratado (régua do MRR, #679 D15), na
 * moeda nativa e em USD — a soma da coluna conversa com os gráficos de
 * receita.
 */
export type CreatedTenantRow = {
  tenantId: string;
  tenantName: string;
  countryCode: string;
  currency: Currency;
  priceCents: number;
  usdEqCents: number;
  createdAt: number;
};

/**
 * Linha da tabela de cancelados. `priceCents`/`usdEqCents` = a receita
 * direta mensal que o tenant levou embora (0 para cortesia, demo, parceiro e
 * test mode, que não eram receita).
 */
export type CancelledTenantRow = {
  tenantId: string;
  tenantName: string;
  countryCode: string;
  currency: Currency;
  priceCents: number;
  usdEqCents: number;
  suspendedAt: number;
  lifetimeDays: number | null;
};

export type SalesMetrics = {
  revenueByCountryUsdCents: Record<string, number>;
  revenueByPlanUsdCents: Record<string, number>;
  newSubscriptionsByDay: { yyyymmdd: string; count: number }[];
  cancellationsByDay: { yyyymmdd: string; count: number }[];
  churnRateByDay: { yyyymmdd: string; pct: number }[];
  createdTenants: CreatedTenantRow[];
  cancelledTenants: CancelledTenantRow[];
};

export type SalesMetricsState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; metrics: SalesMetrics };

function utcMsToYyyymmddBrt(utcMs: number): string {
  const brt = new Date(utcMs - BRT_OFFSET_MS);
  return brt.toISOString().slice(0, 10).replace(/-/g, '');
}

/**
 * Hook do dashboard `/dashboard/sales`.
 *
 * Origem: sem issue — commit 8e8dea1c0, 2026-05-04 (rota /dashboard/sales).
 *
 * Por quê: os gráficos e as tabelas de vendas leem o Firestore direto (o
 * agregado diário não é recortável pelo período/filtros da tela).
 *
 * Contrato: `period` (datas BRT) → `SalesMetricsState`. Combina:
 *  - `billing/subscription` ACTIVE (collection group `billing`) → receita
 *    DIRETA mensal por país e por plano, em USD eq. (snapshot atual, não
 *    histórico) e o denominador do churn;
 *  - `billing/subscription` ACTIVE com `created_at` no período e venda direta
 *    real (`isDirectLiveSale`) → novas assinaturas por dia + tabela;
 *  - `audit` `TENANT_SUSPENDED` no período → cancelamentos por dia + tabela,
 *    com o preço lido do `billing/subscription` de cada cancelado (qualquer
 *    status — ele já não está ACTIVE);
 *  - churn diário = cancelamentos[d] / assinaturas ACTIVE atuais (todas, de
 *    qualquer tipo comercial — a mesma população do numerador, que conta toda
 *    suspensão). Aproximação: denominador constante, sem snapshot por dia.
 *  - Tenant de TESTE (#1008) sai de tudo — receita, criados, cancelados e as
 *    duas pontas do churn — por `isReportExcludedTenant` (cadastro OU
 *    assinatura `livemode === false`).
 * Erro de query → `ERROR` + `reportNonFatal('admin_sales_metrics_failed')`.
 * Doc de assinatura fora do schema → descartado + `admin_subscription_doc_dropped`.
 *
 * Usado por: `SalesDashboardPage.tsx` (rota `/dashboard/sales` em
 * `routes/router.tsx`; web `apps/admin`, super-admin).
 *
 * Arquitetura: receita pela régua única de `subscriptionRevenue.ts` (espelho
 * de `revenuePredicates.ts` das Functions). Só leitura — nenhuma escrita sai
 * daqui. As três collection-group queries são autorizadas por
 * `match /{path=**}/billing/{billingDoc}` e `match /{path=**}/audit/{auditId}`
 * de `firebase/firestore.rules` (só `isSuperAdmin()`), e cada uma tem índice
 * em `firebase/firestore.indexes.json`:
 *  - `billing` `status ==` sozinho → `fieldOverrides` `billing.status`
 *    (escopo COLLECTION_GROUP; o índice automático só cobre COLLECTION);
 *  - `billing` `status ==` + `created_at` intervalo → composto CG
 *    `status ASC, created_at ASC`;
 *  - `audit` `action ==` + `at` intervalo → composto CG `action ASC, at ASC`.
 * Mudar filtro ou ordenação exige índice novo no mesmo commit.
 *
 * Dados (anonimizado; schema em `subscriptionRevenue.ts`):
 *   tenants/{tenantId}/billing/subscription
 *     { "status": "ACTIVE", "plan_id": "STANDARD",
 *       "total_amount_cents": 12990, "amount": 12990, "currency": "BRL",
 *       "billing_cycle": "monthly", "created_at": 1759300000000,
 *       "country_code": "BR" }
 *     — valor em CENTAVOS por ciclo (`total_amount_cents ?? amount`, este
 *       LEGACY); `created_at` em ms (número, não Timestamp — por isso o
 *       filtro compara com `startUtcMs`).
 *   audit/{auditId}
 *     { "action": "TENANT_SUSPENDED", "targetId": "tenant-1", "at": <Timestamp> }
 *     — `at` é Timestamp (aceita número legado; ausente cai em `Date.now()`).
 *   tenants/{tenantId} → `name`, `countryCode` (imutável), `createdAt` (ms).
 * O período chega em datas BRT e vira UTC com offset fixo de 3 h
 * (`brtRangeUtcMs`) — sem horário de verão, que o Brasil não tem desde 2019.
 *
 * Falhas conhecidas:
 *  - 2026-05-19 → 2026-10-03 (#795, ADM-4): lia `billing/current` +
 *    `priceCents`, doc órfão desde `e9692f87e` — receita vazia, "0 novas
 *    assinaturas" e churn 0 % sem aviso.
 *  - Mesmo período: o preço dos cancelados vinha do mapa de ACTIVE, onde um
 *    tenant suspenso nunca está — sempre US$ 0,00.
 *
 *  - até 2026-10-19 (#1008): tenant de teste contava como venda (o DIRECT
 *    sem gateway e sem `livemode`, ex. o cadastro antigo do onboarding) e
 *    toda suspensão de sandbox entrava em cancelamentos e churn.
 *
 * Issues: #795 (ADM-4), #679 (D15 — receita direta), #463 (test mode), #1008
 * (tenant de teste fora dos painéis).
 *
 * Histórico:
 *  - 2026-10-03 14:12 +0200 — (#795, ADM-4) passa a ler `billing/subscription`.
 *  - 2026-10-19 20:57 +0200 — (#1008) exclui tenant de teste de receita,
 *    criados, cancelados e churn.
 */
export function useSalesMetrics(period: Period): SalesMetricsState {
  const [state, setState] = useState<SalesMetricsState>({ kind: 'LOADING' });

  const periodFrom = period.from;
  const periodTo = period.to;

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });
    const { startUtcMs, endUtcMs } = brtRangeUtcMs({
      preset: 'CUSTOM',
      from: periodFrom,
      to: periodTo,
    });

    async function load(): Promise<void> {
      try {
        // Bug corrigido em 2026-10-03 14:12 +0200 (#795, ADM-4): o filtro de
        // caminho era `billing/current` e o valor `priceCents` — doc que
        // nenhum escritor grava desde 2026-05-19 (`e9692f87e`). Toda linha
        // era descartada e os gráficos ficavam vazios sem erro. Não voltar a
        // ler `billing/current`: a fonte é `billing/subscription`, pela régua
        // de `subscriptionRevenue.ts`.
        const drops: SubscriptionDrop[] = [];
        const activeSnap = await getDocs(
          query(
            collectionGroup(firestore, 'billing'),
            where('status', '==', SubscriptionStatus.ACTIVE),
          ),
        );
        // #1008: tenant de teste fora das duas pontas — receita por
        // país/plano e denominador do churn (o numerador também o exclui
        // abaixo; as duas pontas têm de ser a mesma população).
        const activeSubs = new Map(
          Array.from(parseSubscriptionDocs(activeSnap.docs, drops)).filter(
            ([tid, f]) => !isReportExcludedTenant(tid, f),
          ),
        );

        const tenantIds = new Set<string>();
        const revenueByTenant = new Map<string, { usdEqCents: number; planId: string; countryCode: string | null }>();
        for (const [tid, f] of activeSubs) {
          const revenue = directMonthlyRevenue(f);
          if (!revenue) continue;
          revenueByTenant.set(tid, {
            usdEqCents: revenue.usdEqCents,
            planId: f.plan_id ?? 'STANDARD',
            countryCode: f.country_code ?? null,
          });
          tenantIds.add(tid);
        }

        // Assinaturas criadas no período — mesma query e mesma régua do
        // `sales` do `aggregateDailyMetrics` (venda direta real, sem exigir
        // pagamento). Índice `billing` CG (status, created_at).
        const createdSnap = await getDocs(
          query(
            collectionGroup(firestore, 'billing'),
            where('created_at', '>=', startUtcMs),
            where('created_at', '<', endUtcMs),
            where('status', '==', SubscriptionStatus.ACTIVE),
          ),
        );
        const createdInterim: { tenantId: string; fields: SubscriptionRevenueFields; createdAt: number }[] = [];
        for (const [tid, f] of parseSubscriptionDocs(createdSnap.docs, drops)) {
          if (!isDirectLiveSale(f) || typeof f.created_at !== 'number') continue;
          // #1008: `isDirectLiveSale` só olha o `livemode`; o tenant de teste
          // cadastrado sem `livemode` (DIRECT sem gateway) passava como venda.
          if (isReportExcludedTenant(tid, f)) continue;
          createdInterim.push({ tenantId: tid, fields: f, createdAt: f.created_at });
          tenantIds.add(tid);
        }

        // Cancelamentos no período via audit.
        const auditSnap = await getDocs(
          query(
            collectionGroup(firestore, 'audit'),
            where('action', '==', AuditAction.TENANT_SUSPENDED),
            where('at', '>=', Timestamp.fromMillis(startUtcMs)),
            where('at', '<', Timestamp.fromMillis(endUtcMs)),
          ),
        );
        const auditCancelled: { tenantId: string; suspendedAt: number }[] = [];
        for (const d of auditSnap.docs) {
          const tid: unknown = d.get('targetId');
          if (typeof tid !== 'string') continue;
          // #1008: o tenant de teste cadastrado sai antes de gastar a leitura
          // da assinatura; o sandbox não cadastrado sai depois dela.
          if (isReportExcludedTenant(tid)) continue;
          const at: unknown = d.get('at');
          const ms =
            at instanceof Timestamp ? at.toMillis() : typeof at === 'number' ? at : serverNow();
          auditCancelled.push({ tenantId: tid, suspendedAt: ms });
        }

        // Assinatura de cada cancelado, em QUALQUER status: o mapa de ACTIVE
        // não serve — o tenant suspenso já saiu dele (era assim que a linha
        // saía sempre com US$ 0,00, #795 ADM-4).
        const cancelledSubs = new Map<string, SubscriptionRevenueFields>();
        const cancelledIds = Array.from(new Set(auditCancelled.map((c) => c.tenantId)));
        const cancelledSnaps = await Promise.all(
          cancelledIds.map((tid) => getDoc(doc(firestore, 'tenants', tid, 'billing', 'subscription'))),
        );
        for (const snap of cancelledSnaps) {
          if (!snap.exists()) continue;
          const parsed = parseSubscriptionDoc(snap.ref.path, snap.data());
          if (parsed === null) continue;
          if (parsed.ok) cancelledSubs.set(parsed.tenantId, parsed.fields);
          else drops.push(parsed.drop);
        }
        // #1008: suspensão de tenant sandbox (`livemode: false`) não é
        // cancelamento — era o que inflava o churn a cada e2e.
        const cancelledInterim = auditCancelled.filter(
          (c) => !isReportExcludedTenant(c.tenantId, cancelledSubs.get(c.tenantId) ?? null),
        );
        for (const c of cancelledInterim) tenantIds.add(c.tenantId);

        // Lookup tenants em batch (whereIn 30 por chunk).
        // `countryCode: null` quando o tenant não tem o campo — para o `??`
        // abaixo cair no `country_code` congelado na assinatura.
        const tenantById = new Map<string, { name: string; countryCode: string | null; createdAt: number | null }>();
        const tenantList = Array.from(tenantIds);
        for (let i = 0; i < tenantList.length; i += 30) {
          const slice = tenantList.slice(i, i + 30);
          if (slice.length === 0) continue;
          const tenantSnap = await getDocs(
            query(collection(firestore, 'tenants'), where('__name__', 'in', slice)),
          );
          for (const t of tenantSnap.docs) {
            const data = t.data() as Record<string, unknown>;
            tenantById.set(t.id, {
              name: typeof data.name === 'string' ? data.name : t.id,
              countryCode:
                typeof data.countryCode === 'string' && data.countryCode !== '' ? data.countryCode : null,
              createdAt: typeof data.createdAt === 'number' ? data.createdAt : null,
            });
          }
        }

        // Receita por país e plano (snapshot atual da receita direta).
        // País: o do tenant (`countryCode`, imutável) e, sem ele, o
        // `country_code` congelado na assinatura.
        const revenueByCountry: Record<string, number> = {};
        const revenueByPlan: Record<string, number> = {};
        for (const [tid, r] of revenueByTenant) {
          const country = tenantById.get(tid)?.countryCode ?? r.countryCode ?? 'UNKNOWN';
          revenueByCountry[country] = (revenueByCountry[country] ?? 0) + r.usdEqCents;
          revenueByPlan[r.planId] = (revenueByPlan[r.planId] ?? 0) + r.usdEqCents;
        }

        // Created tenants enriquecidos.
        const createdTenants: CreatedTenantRow[] = createdInterim
          .map((c): CreatedTenantRow => {
            const value = monthlyValue(c.fields);
            const t = tenantById.get(c.tenantId);
            return {
              tenantId: c.tenantId,
              tenantName: t?.name ?? c.tenantId,
              countryCode: t?.countryCode ?? c.fields.country_code ?? '',
              currency: c.fields.currency,
              priceCents: value?.monthlyCents ?? 0,
              usdEqCents: value?.usdEqCents ?? 0,
              createdAt: c.createdAt,
            };
          })
          .sort((a, b) => b.createdAt - a.createdAt);

        // Cancelled tenants enriquecidos com lifetime.
        const cancelledTenants: CancelledTenantRow[] = cancelledInterim
          .map((c): CancelledTenantRow => {
            const sub = cancelledSubs.get(c.tenantId);
            const lost = sub ? directMonthlyRevenue(sub, { anyStatus: true }) : null;
            const t = tenantById.get(c.tenantId);
            const lifetime =
              t?.createdAt != null
                ? Math.max(0, Math.floor((c.suspendedAt - t.createdAt) / (24 * 60 * 60 * 1000)))
                : null;
            return {
              tenantId: c.tenantId,
              tenantName: t?.name ?? c.tenantId,
              countryCode: t?.countryCode ?? sub?.country_code ?? '',
              currency: sub?.currency ?? Currency.USD,
              priceCents: lost?.monthlyCents ?? 0,
              usdEqCents: lost?.usdEqCents ?? 0,
              suspendedAt: c.suspendedAt,
              lifetimeDays: lifetime,
            };
          })
          .sort((a, b) => b.suspendedAt - a.suspendedAt);

        // Buckets diários.
        const newSubsBucket = new Map<string, number>();
        for (const c of createdInterim) {
          const day = utcMsToYyyymmddBrt(c.createdAt);
          newSubsBucket.set(day, (newSubsBucket.get(day) ?? 0) + 1);
        }
        const cancelBucket = new Map<string, number>();
        for (const c of cancelledInterim) {
          const day = utcMsToYyyymmddBrt(c.suspendedAt);
          cancelBucket.set(day, (cancelBucket.get(day) ?? 0) + 1);
        }

        const allDays = new Set<string>([
          ...newSubsBucket.keys(),
          ...cancelBucket.keys(),
        ]);
        const sortedDays = Array.from(allDays).sort();
        const newSubscriptionsByDay = sortedDays.map((day) => ({
          yyyymmdd: day,
          count: newSubsBucket.get(day) ?? 0,
        }));
        const cancellationsByDay = sortedDays.map((day) => ({
          yyyymmdd: day,
          count: cancelBucket.get(day) ?? 0,
        }));

        // Denominador = TODAS as assinaturas ACTIVE (qualquer tipo
        // comercial): o numerador conta toda suspensão do audit, e as duas
        // pontas têm de ser a mesma população — numerador amplo sobre
        // denominador só de pagantes dava churn de 100 % (lição da #679 F7).
        const activeDenominator = activeSubs.size;
        const churnRateByDay = sortedDays.map((day) => ({
          yyyymmdd: day,
          pct: activeDenominator === 0 ? 0 : (cancelBucket.get(day) ?? 0) / activeDenominator,
        }));

        if (guard.cancelled) return;
        reportSubscriptionDrops('sales', drops);
        setState({
          kind: 'READY',
          metrics: {
            revenueByCountryUsdCents: revenueByCountry,
            revenueByPlanUsdCents: revenueByPlan,
            newSubscriptionsByDay,
            cancellationsByDay,
            churnRateByDay,
            createdTenants,
            cancelledTenants,
          },
        });
      } catch (err) {
        if (guard.cancelled) return;
        reportNonFatal('admin_sales_metrics_failed', serializeError(err));
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [periodFrom, periodTo]);

  return state;
}
